import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "motion/react";
import { Page, Screen, ScreenHeader, Section, StickyCta } from "../components/AppShell";
import { Button, Chip, Skeleton, Spinner, StateMessage } from "../components/ui";
import { Icon } from "../components/Icon";
import { useToast } from "../components/Toast";
import { Field, NumberInput, Segmented, TextArea, TextInput } from "../components/fields";
import { Picker } from "../components/Picker";
import {
  FileSlot,
  MediaSlot,
  useRevokeOnUnmount,
  type StagedFile,
} from "../features/media/MediaPicker";
import { StonesEditor } from "../features/pieces/StonesEditor";
import { PriceBreakdown } from "../features/prices/PriceBreakdown";
import { quote } from "../features/prices/quote";
import { blankForm, formFromPiece, toPayload, usePieceForm } from "../features/pieces/usePieceForm";
import {
  useCreatePiece,
  useDeleteAttachment,
  useMakePrimary,
  useMe,
  usePiece,
  usePrices,
  useUpdatePiece,
  useUploadedMedia,
} from "../api/queries";
import { uploadAttachment } from "../api/upload";
import { setClosingConfirmation } from "../telegram/webapp";
import { haptic } from "../telegram/haptics";
import { listItem, sectionStagger } from "../motion";
import { fieldLabels } from "../i18n/fieldLabels";
import {
  PURITY_PRESETS,
  conditionLabels,
  kindLabels,
  metalColorLabels,
  metalLabels,
  optionsOf,
  ownershipLabels,
} from "../i18n/choices";
import { pick } from "../i18n/labels";
import { useT } from "../i18n/strings";
import { messagesOf } from "../api/errors";
import type {
  AttachmentKind,
  Condition,
  Metal,
  MetalColor,
  OwnershipStatus,
  PieceKind,
} from "../api/types";
import s from "../features/pieces/form.module.css";

/** Order is fixed; the labels are resolved per render. */
const YEAR_VALUES = ["exact", "circa", "range", "unknown"] as const;

export default function PieceForm() {
  const t = useT();
  const yearOptions = YEAR_VALUES.map((value) => ({
    value,
    label: t.form.yearPrecision[value],
  }));
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const me = useMe();
  const existing = usePiece(id);
  const prices = usePrices();

  const form = usePieceForm(blankForm);
  const { state, set, reset, errors, dirty, check, applyServerErrors } = form;

  const [staged, setStaged] = useState<StagedFile[]>([]);
  /**
   * Which media file is the thumbnail: a staged file's key, or a saved
   * attachment's id. `save` settles it with one `make-primary` call once
   * everything that had to upload has uploaded.
   */
  const [primaryKey, setPrimaryKey] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useRevokeOnUnmount(staged);

  const create = useCreatePiece();
  const update = useUpdatePiece(id ?? "");
  const uploaded = useUploadedMedia();
  const removeAttachment = useDeleteAttachment(id ?? "");
  const makePrimary = useMakePrimary(id ?? "");

  // Load the record into the form exactly once, not on every background refetch.
  const loadedId = existing.data?.id;
  const savedPrimaryId =
    existing.data?.attachments.find((file) => file.kind === "primary")?.id ?? null;
  useEffect(() => {
    if (existing.data && loadedId === id) {
      reset(formFromPiece(existing.data));
      setPrimaryKey(existing.data.attachments.find((file) => file.kind === "primary")?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedId]);

  // Telegram closes on a swipe-down; unsaved work deserves a prompt first.
  const thumbnailMoved = primaryKey !== savedPrimaryId;
  useEffect(() => {
    setClosingConfirmation((dirty || thumbnailMoved || staged.length > 0) && !saved);
    return () => setClosingConfirmation(false);
  }, [dirty, thumbnailMoved, staged.length, saved]);

  const stageFiles = useCallback(
    (files: StagedFile[]) => setStaged((current) => [...current, ...files]),
    [],
  );

  const unstage = useCallback((key: string) => {
    setPrimaryKey((current) => (current === key ? null : current));
    setStaged((current) => {
      const target = current.find((item) => item.key === key);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return current.filter((item) => item.key !== key);
    });
  }, []);

  const deleteExisting = useCallback(
    (attachmentId: string) => {
      setPrimaryKey((current) => (current === attachmentId ? null : current));
      removeAttachment.mutate(attachmentId, {
        onError: (error) => toast.show(messagesOf(error)[0] ?? t.errors.title, { tone: "error" }),
      });
    },
    [removeAttachment, toast, t],
  );

  const attachmentsOf = (kind: AttachmentKind) =>
    existing.data?.attachments.filter((file) => file.kind === kind) ?? [];
  const mediaAttachments = [...attachmentsOf("primary"), ...attachmentsOf("gallery")];
  const stagedOf = (kind: AttachmentKind) => staged.filter((item) => item.kind === kind);

  /** Media is signed against a piece that exists, so a new record is written first. */
  const runUploads = async (pieceId: string) => {
    const landed = new Map<string, string>();
    for (const item of staged) {
      setProgress((current) => ({ ...current, [item.key]: 0 }));
      try {
        const attachment = await uploadAttachment({
          pieceId,
          kind: item.kind,
          file: item.file,
          onProgress: (fraction) =>
            setProgress((current) => ({ ...current, [item.key]: fraction })),
        });
        uploaded.absorb(pieceId, attachment);
        landed.set(item.key, attachment.id);
      } catch (error) {
        toast.show(`${item.file.name}: ${messagesOf(error)[0]}`, { tone: "error" });
      } finally {
        setProgress((current) => {
          const next = { ...current };
          delete next[item.key];
          return next;
        });
      }
    }
    // `useRevokeOnUnmount` only sees what is still staged; revoke the rest here.
    setStaged((current) => {
      for (const item of current) if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return [];
    });
    return landed;
  };

  const save = async () => {
    if (saving) return;
    if (!check()) {
      haptic.error();
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(state);
      const piece = editing ? await update.mutateAsync(payload) : await create.mutateAsync(payload);

      const landed = staged.length ? await runUploads(piece.id) : new Map<string, string>();

      // Everything uploads as gallery; the thumbnail is settled with one promotion.
      // A staged file whose upload failed has no id and cannot take the mark.
      const stagedKey = staged.some((item) => item.key === primaryKey);
      const heroId = primaryKey
        ? (landed.get(primaryKey) ?? (stagedKey ? null : primaryKey))
        : null;
      if (heroId && heroId !== savedPrimaryId) {
        await makePrimary.mutateAsync(heroId);
      }
      if (landed.size > 0 || (heroId && heroId !== savedPrimaryId)) {
        await uploaded.settle(piece.id);
      }

      setSaved(true);
      haptic.success();
      setClosingConfirmation(false);
      navigate(`/piece/${piece.id}`, { replace: true });
    } catch (error) {
      applyServerErrors(error);
      haptic.error();
      toast.show(messagesOf(error)[0] ?? t.errors.title, { tone: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (editing && existing.isPending) return <FormSkeleton />;

  if (editing && existing.error) {
    return (
      <Screen withTabs={false}>
        <ScreenHeader title={t.form.editTitle} />
        <StateMessage
          icon="alert"
          title={t.errors.title}
          body={messagesOf(existing.error)[0]}
          action={
            <Button variant="secondary" onClick={() => navigate(-1)}>
              {t.actions.back}
            </Button>
          }
        />
      </Screen>
    );
  }

  const limits = me.data?.limits;
  const presets = state.metal ? (PURITY_PRESETS[state.metal] ?? []) : [];
  // Still sold as saved: the price stays where the sale fixed it.
  const record = existing.data;
  const frozen =
    record?.sold_price_rial != null && state.ownership_status === "sold"
      ? { rial: record.sold_price_rial, at: record.sold_priced_at }
      : null;

  return (
    <Screen withTabs={false}>
      <ScreenHeader sticky bordered title={editing ? t.form.editTitle : t.form.newTitle} />

      <Page>
        <motion.div variants={sectionStagger} initial="hidden" animate="show" className={s.form}>
          {/* ---------------------------------------------------- identity */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.identity} icon="pieces">
              <Field label={fieldLabels.title} error={errors.title} htmlFor="title">
                <TextInput
                  id="title"
                  auto
                  value={state.title}
                  invalid={Boolean(errors.title)}
                  placeholder={t.form.titlePlaceholder}
                  onChange={(value) => set("title", value)}
                />
              </Field>

              <Field label={fieldLabels.kind} error={errors.kind}>
                <Picker
                  title={pick(fieldLabels.kind)}
                  options={optionsOf(kindLabels)}
                  value={state.kind || null}
                  clearable={false}
                  onChange={(value) => set("kind", (value ?? "") as PieceKind | "")}
                />
              </Field>

              <Field
                label={fieldLabels.inventory_code}
                optional
                error={errors.inventory_code}
                htmlFor="inventory"
              >
                <TextInput
                  id="inventory"
                  value={state.inventory_code}
                  placeholder={t.form.inventoryPlaceholder}
                  onChange={(value) => set("inventory_code", value)}
                />
              </Field>
            </Section>
          </motion.div>

          {/* ------------------------------------------------------- media */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.media} icon="image">
              <Field label={fieldLabels.primary_media} optional>
                <MediaSlot
                  existing={mediaAttachments}
                  staged={stagedOf("gallery")}
                  primaryKey={primaryKey}
                  onPickPrimary={setPrimaryKey}
                  limits={limits}
                  artworkId={id ?? null}
                  progress={progress}
                  onStage={stageFiles}
                  onUnstage={unstage}
                  onDeleteExisting={deleteExisting}
                />
              </Field>
            </Section>
          </motion.div>

          {/* ------------------------------------------------------- metal */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.metal} icon="metal">
              <Field label={fieldLabels.metal} optional>
                <Picker
                  title={pick(fieldLabels.metal)}
                  options={optionsOf(metalLabels)}
                  value={state.metal || null}
                  placeholder={t.form.none}
                  onChange={(value) => set("metal", (value ?? "") as Metal | "")}
                />
              </Field>

              <Field label={fieldLabels.metal_color} optional>
                <Picker
                  title={pick(fieldLabels.metal_color)}
                  options={optionsOf(metalColorLabels)}
                  value={state.metal_color || null}
                  placeholder={t.form.none}
                  onChange={(value) => set("metal_color", (value ?? "") as MetalColor | "")}
                />
              </Field>

              <Field
                label={fieldLabels.purity_permille}
                optional
                error={errors.purity_permille}
                hint={t.form.purityHint}
              >
                <NumberInput
                  step="1"
                  min={1}
                  value={state.purity_permille}
                  invalid={Boolean(errors.purity_permille)}
                  onChange={(value) => set("purity_permille", value)}
                />
                {presets.length > 0 && (
                  <div className={s.presets}>
                    {presets.map(([permille, karat]) => (
                      <Chip
                        key={permille}
                        selected={state.purity_permille === permille}
                        onClick={() => set("purity_permille", permille)}
                      >
                        <span className="mono">{karat ? `${karat}k · ${permille}` : permille}</span>
                      </Chip>
                    ))}
                  </div>
                )}
              </Field>

              <div className={s.yearRow}>
                <Field
                  label={fieldLabels.gross_weight_g}
                  optional
                  error={errors.gross_weight_g}
                  hint={t.form.grossHint}
                >
                  <div className={s.unitRow}>
                    <NumberInput
                      min={0}
                      value={state.gross_weight_g}
                      invalid={Boolean(errors.gross_weight_g)}
                      onChange={(value) => set("gross_weight_g", value)}
                    />
                    <span className={s.unit}>{t.form.gram}</span>
                  </div>
                </Field>
                <Field
                  label={fieldLabels.net_weight_g}
                  optional
                  error={errors.net_weight_g}
                  hint={t.form.netHint}
                >
                  <div className={s.unitRow}>
                    <NumberInput
                      min={0}
                      value={state.net_weight_g}
                      invalid={Boolean(errors.net_weight_g)}
                      onChange={(value) => set("net_weight_g", value)}
                    />
                    <span className={s.unit}>{t.form.gram}</span>
                  </div>
                </Field>
              </div>

              <Field label={fieldLabels.hallmarks} optional>
                <TextInput
                  auto
                  value={state.hallmarks}
                  placeholder={t.form.hallmarksPlaceholder}
                  onChange={(value) => set("hallmarks", value)}
                />
              </Field>
            </Section>
          </motion.div>

          {/* ------------------------------------------------------ stones */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.stones} icon="stone">
              <StonesEditor
                stones={state.stones}
                errors={errors}
                board={prices.data}
                onChange={form.setStone}
                onAdd={form.addStone}
                onRemove={form.removeStone}
              />
            </Section>
          </motion.div>

          {/* -------------------------------------------------------- make */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.make} icon="maker">
              <Field label={fieldLabels.maker} optional>
                <TextInput
                  auto
                  value={state.maker}
                  placeholder={t.form.makerPlaceholder}
                  onChange={(value) => set("maker", value)}
                />
              </Field>

              <Field label={fieldLabels.size_label} optional>
                <TextInput
                  auto
                  value={state.size_label}
                  placeholder={t.form.sizePlaceholder}
                  onChange={(value) => set("size_label", value)}
                />
              </Field>

              <Field label={fieldLabels.year} optional>
                <Segmented
                  name="year-precision"
                  value={state.year_precision}
                  options={yearOptions}
                  onChange={(value) =>
                    form.patch(
                      value === "unknown"
                        ? { year_precision: value, year_start: null, year_end: null }
                        : { year_precision: value },
                    )
                  }
                />
              </Field>

              {state.year_precision !== "unknown" && (
                <div className={s.yearRow}>
                  <Field
                    label={
                      state.year_precision === "range"
                        ? { fa: t.form.yearFrom, en: t.form.yearFrom }
                        : fieldLabels.year
                    }
                    error={errors.year_start}
                  >
                    <NumberInput
                      step="1"
                      value={state.year_start}
                      invalid={Boolean(errors.year_start)}
                      onChange={(value) => set("year_start", value)}
                    />
                  </Field>

                  {state.year_precision === "range" && (
                    <Field label={{ fa: t.form.yearTo, en: t.form.yearTo }} error={errors.year_end}>
                      <NumberInput
                        step="1"
                        value={state.year_end}
                        invalid={Boolean(errors.year_end)}
                        onChange={(value) => set("year_end", value)}
                      />
                    </Field>
                  )}
                </div>
              )}
            </Section>
          </motion.div>

          {/* --------------------------------------------- stock and price */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.ownership} icon="ownership">
              <Field label={fieldLabels.ownership_status} optional>
                <Picker
                  title={pick(fieldLabels.ownership_status)}
                  options={optionsOf(ownershipLabels)}
                  value={state.ownership_status || null}
                  placeholder={t.form.none}
                  onChange={(value) =>
                    set("ownership_status", (value ?? "") as OwnershipStatus | "")
                  }
                />
              </Field>

              <Field
                label={fieldLabels.making_charge_percent}
                optional
                error={errors.making_charge_percent}
                hint={t.form.makingHint}
              >
                <div className={s.unitRow}>
                  <NumberInput
                    min={0}
                    value={state.making_charge_percent}
                    invalid={Boolean(errors.making_charge_percent)}
                    onChange={(value) => set("making_charge_percent", value)}
                  />
                  <span className={s.unit}>{t.form.percent}</span>
                </div>
              </Field>

              <Field label={{ fa: t.detail.price, en: t.detail.price }}>
                <PriceBreakdown
                  quote={quote(state, prices.data)}
                  makingPercent={state.making_charge_percent}
                  board={prices.data}
                  loading={prices.isPending}
                  frozen={frozen}
                  hint={t.detail.priceHint}
                />
              </Field>
            </Section>
          </motion.div>

          {/* --------------------------------------------------- condition */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.condition} icon="condition">
              <Field label={fieldLabels.condition} optional>
                <Picker
                  title={pick(fieldLabels.condition)}
                  options={optionsOf(conditionLabels)}
                  value={state.condition || null}
                  placeholder={t.form.none}
                  onChange={(value) => set("condition", (value ?? "") as Condition | "")}
                />
              </Field>

              <Field label={fieldLabels.condition_notes} optional>
                <TextArea
                  value={state.condition_notes}
                  onChange={(value) => set("condition_notes", value)}
                />
              </Field>

              <FileSlot
                kind="condition_report"
                addLabel={t.media.pickFile}
                existing={attachmentsOf("condition_report")}
                staged={stagedOf("condition_report")}
                limits={limits}
                artworkId={id ?? null}
                progress={progress}
                onStage={stageFiles}
                onUnstage={unstage}
                onDeleteExisting={deleteExisting}
              />
            </Section>
          </motion.div>

          {/* ------------------------------------------ provenance + notes */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.provenance} icon="provenance">
              <Field label={fieldLabels.provenance_text} optional>
                <TextArea
                  value={state.provenance_text}
                  onChange={(value) => set("provenance_text", value)}
                />
              </Field>
              <Field label={fieldLabels.notes} optional>
                <TextArea value={state.notes} onChange={(value) => set("notes", value)} />
              </Field>
            </Section>
          </motion.div>

          {/* ---------------------------------------------------- evidence */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.authenticity} icon="authenticity">
              {/* Evidence, never a verdict. */}
              <p className={s.note}>
                <Icon name="info" size={16} />
                <span>{t.detail.authenticityNote}</span>
              </p>

              <Field label={fieldLabels.authenticity_notes} optional>
                <TextArea
                  value={state.authenticity_notes}
                  onChange={(value) => set("authenticity_notes", value)}
                />
              </Field>

              <Field label={fieldLabels.authenticity_files} optional>
                <FileSlot
                  kind="authenticity_evidence"
                  addLabel={t.media.pickFile}
                  existing={attachmentsOf("authenticity_evidence")}
                  staged={stagedOf("authenticity_evidence")}
                  limits={limits}
                  artworkId={id ?? null}
                  progress={progress}
                  onStage={stageFiles}
                  onUnstage={unstage}
                  onDeleteExisting={deleteExisting}
                />
              </Field>
            </Section>
          </motion.div>

          <div className={s.ctaSpacer} />
        </motion.div>
      </Page>

      <StickyCta>
        <Button variant="primary" onClick={save} disabled={saving}>
          {saving ? (
            <>
              <Spinner size={20} />
              {staged.length ? t.media.uploading : t.actions.saving}
            </>
          ) : (
            <>
              <Icon name="check" size={20} />
              {t.actions.save}
            </>
          )}
        </Button>
      </StickyCta>
    </Screen>
  );
}

function FormSkeleton() {
  const t = useT();
  return (
    <Screen withTabs={false}>
      <ScreenHeader sticky bordered title={t.form.editTitle} />
      <Page>
        <div className={s.form}>
          {Array.from({ length: 5 }, (_, index) => (
            <div key={index} className={s.skeletonBlock}>
              <Skeleton height={14} width="30%" radius="4px" />
              <Skeleton height={44} radius="var(--radius-card)" />
            </div>
          ))}
        </div>
      </Page>
    </Screen>
  );
}
